import React, { useState } from 'react';
import { checkKeywordRankingApi, setProjectScheduleApi } from '../apis/projects.api';
import { toast } from 'react-toastify';

/**
 * Component for checking keyword rankings manually or setting up auto-check schedules
 * 
 * Props:
 * - projectId: The project ID
 * - keyword: The keyword to track
 * - currentSchedule: Current schedule setting (if any)
 */
const KeywordRankingManager = ({ projectId, keyword, currentSchedule = 'disabled' }) => {
  const [loading, setLoading] = useState(false);
  const [rankData, setRankData] = useState(null);
  const [schedule, setSchedule] = useState(currentSchedule);
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [updatingSchedule, setUpdatingSchedule] = useState(false);

  // Manual rank check (costs 5 credits)
  const handleCheckRank = async () => {
    setLoading(true);
    try {
      const response = await checkKeywordRankingApi({ project_id: projectId });
      setRankData(response.data);
      
      const { old_rank, new_rank, remaining_credits } = response.data;
      
      if (old_rank !== null && new_rank !== null && old_rank !== new_rank) {
        const direction = new_rank < old_rank ? 'up ⬆️' : 'down ⬇️';
        toast.success(
          `Ranking moved ${direction}: Position ${old_rank} → ${new_rank}`,
          { autoClose: 5000 }
        );
      } else if (new_rank) {
        toast.info(`Current ranking: Position ${new_rank}`);
      } else {
        toast.warning('Keyword not found in top 100 results');
      }
      
      toast.info(`Remaining credits: ${remaining_credits}`);
    } catch (error) {
      const errorCode = error.response?.data?.error;
      
      if (errorCode === 'NO_CREDITS') {
        toast.error('❌ Insufficient credits. Please recharge your account.');
      } else if (errorCode === 'PLAN_EXPIRED') {
        toast.error('❌ Your plan has expired. Please renew.');
      } else if (errorCode === 'NO_PLAN') {
        toast.error('❌ No active plan. Please subscribe first.');
      } else {
        toast.error('Failed to check keyword ranking');
      }
    } finally {
      setLoading(false);
    }
  };

  // Update automatic check schedule
  const handleUpdateSchedule = async () => {
    setUpdatingSchedule(true);
    try {
      const payload = {
        schedule: schedule,
      };
      
      if (schedule !== 'disabled') {
        payload.time = scheduleTime;
      }

      await setProjectScheduleApi(projectId, payload);
      
      if (schedule === 'disabled') {
        toast.success('Auto-check disabled');
      } else {
        toast.success(`Auto-check scheduled ${schedule} at ${scheduleTime}`);
      }
    } catch (error) {
      toast.error('Failed to update schedule');
    } finally {
      setUpdatingSchedule(false);
    }
  };

  return (
    <div className="keyword-ranking-manager p-6 bg-white rounded-lg shadow-md">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Keyword Ranking Tracker</h2>
        <p className="text-gray-600 mt-2">
          Tracking: <span className="font-semibold text-blue-600">{keyword}</span>
        </p>
      </div>

      {/* Manual Check Section */}
      <div className="mb-8 p-4 border border-gray-200 rounded-lg">
        <h3 className="text-lg font-semibold mb-3">Manual Check</h3>
        <button
          onClick={handleCheckRank}
          disabled={loading}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            loading
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          {loading ? (
            <span className="flex items-center">
              <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Checking...
            </span>
          ) : (
            'Check Ranking Now (5 Credits)'
          )}
        </button>

        {/* Results Display */}
        {rankData && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Previous Rank</p>
                <p className="text-2xl font-bold text-gray-800">
                  {rankData.old_rank || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Current Rank</p>
                <p className="text-2xl font-bold text-blue-600">
                  {rankData.new_rank || 'Not in top 100'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Credits Left</p>
                <p className="text-2xl font-bold text-green-600">
                  {rankData.remaining_credits}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Auto-Check Schedule Section */}
      
    </div>
  );
};

export default KeywordRankingManager;
